// Form hook for Driver Trip Calculator
// Based on technical spec validation rules in AC-DTC-01

import { useForm, useFieldArray, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useMemo, useRef } from "react";
import {
  DriverTripCalculatorFormData,
  TripCalculation,
  DEFAULT_LOCATION,
  LocationPairRoute,
} from "_interfaces/driverTripCalculator.interfaces";
import { useTripCalculatorServiceV1CalculateTripMutation } from "_services/modules/trip-calculator";
import {
  toCalculateTripReq,
  fromCalculateTripData,
} from "_services/modules/driverTripCalculator/tripCalculatorMapper";
import { toast } from "react-toastify";
import { useAppTranslation } from "locale/useAppTranslation";
import { useAppDispatch, useAppSelector } from "store";
import {
  setCurrentStep,
  saveFormData,
  setCalculationResult,
  setSegmentRoutes,
  resetDtcState,
  SegmentRoute,
} from "store/driver-trip-calculator";

type TFunction = (key: any, options?: Record<string, unknown>) => string;

// Location point schema
const createLocationSchema = (t: TFunction) => yup.object({
  name: yup.string().required(t("validation-location-required")),
  lat: yup.number().default(0),
  lng: yup.number().default(0),
  avoid_toll: yup.boolean().default(false),
});

// Validation schema
const createSegmentSchema = (t: TFunction, freezerTripTypeId: string) => yup.object({
  segment_order: yup.number().required(),
  trip_type_id: yup.string().required(t("validation-trip-type-required")),
  locations: yup
    .array()
    .of(createLocationSchema(t))
    .min(2, t("validation-locations-min")),
  rest_time: yup.number().default(0),
  freezer_idle_hours: yup
    .number()
    .nullable()
    .when("trip_type_id", {
      is: (val: string) => !!freezerTripTypeId && val === freezerTripTypeId,
      then: (schema) =>
        schema
          .required(t("validation-freezer-required"))
          .min(0.1, t("validation-freezer-min")),
      otherwise: (schema) => schema.notRequired(),
    }),
  enable_additional_cost: yup.boolean().default(false),
  additional_costs: yup.array().of(
    yup.object({
      cost_type: yup.string().required(t("validation-cost-type-required")),
      amount: yup
        .mixed()
        .test(
          "is-positive-number",
          t("validation-amount-positive"),
          function (value) {
            const numValue = typeof value === "string" ? parseFloat(value) : value;
            return !isNaN(numValue as number) && (numValue as number) > 0;
          }
        )
        .required(t("validation-amount-required")),
    })
  ),
});

const createFormSchema = (t: TFunction, freezerTripTypeId: string) => yup.object({
  customer: yup.string().required(t("validation-customer-required")),
  temperature: yup
    .mixed()
    .test("is-number", t("validation-temperature-required"), function (value) {
      if (value === "" || value === undefined || value === null) return false;
      return !isNaN(Number(value));
    })
    .required(t("validation-temperature-required")),
  plate_number: yup.string().required(t("validation-plate-required")),
  vehicle_type_id: yup.string().required(t("validation-vehicle-type-required")),
  vehicle_model_id: yup.string().required(t("validation-vehicle-model-required")),
  segments: yup
    .array()
    .of(createSegmentSchema(t, freezerTripTypeId))
    .min(1, t("validation-segment-min"))
    .max(10, t("validation-segment-max")),
});

const createDefaultSegment = (order: number) => ({
  segment_order: order,
  trip_type_id: "",
  locations: [
    { ...DEFAULT_LOCATION }, // Start point
    { ...DEFAULT_LOCATION }, // End point
  ],
  rest_time: 0,
  freezer_idle_hours: null as number | null,
  enable_additional_cost: false,
  additional_costs: [] as { cost_type: string; amount: number | string }[],
});

export const useDriverTripCalculatorForm = (freezerTripTypeId: string = "") => {
  const { t } = useAppTranslation("driverTripCalculator");
  const formSchema = useMemo(() => createFormSchema(t, freezerTripTypeId), [t, freezerTripTypeId]);

  const [calculateTrip, { isLoading: isCalculating }] =
    useTripCalculatorServiceV1CalculateTripMutation();

  const dispatch = useAppDispatch();
  const currentStep = useAppSelector((state) => state.driverTripCalculator.currentStep);
  const calculationResult = useAppSelector((state) => state.driverTripCalculator.calculationResult);
  const storedFormData = useAppSelector((state) => state.driverTripCalculator.formData);
  // Track distance/time per segment (from Google Maps Directions)
  const segmentRoutes = useAppSelector(
    (state) => state.driverTripCalculator.segmentRoutes,
  ) as Record<number, SegmentRoute>;

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    trigger,
    formState: { errors },
  } = useForm<DriverTripCalculatorFormData>({
    resolver: yupResolver(formSchema),
    mode: "onBlur",
    defaultValues: {
      customer: "",
      temperature: "",
      plate_number: "",
      vehicle_type_id: "",
      vehicle_model_id: "",
      segments: [createDefaultSegment(1)],
    },
  });

  const {
    fields: segmentFields,
    append: appendSegment,
    remove: removeSegment,
  } = useFieldArray({
    control,
    name: "segments",
  });

  // When freezerTripTypeId loads for the first time (API response arrives),
  // re-trigger validation so the schema condition is evaluated with the real ID.
  // We only do this once (prev was "" → now has a value) to avoid spamming
  // error messages on pristine fields.
  const prevFreezerIdRef = useRef(freezerTripTypeId);
  useEffect(() => {
    if (!prevFreezerIdRef.current && freezerTripTypeId) {
      trigger("segments");
    }
    prevFreezerIdRef.current = freezerTripTypeId;
  }, [freezerTripTypeId, trigger]);

  // Restore saved form data from Redux on mount (run once)
  const hasRestoredRef = useRef(false);
  useEffect(() => {
    if (!hasRestoredRef.current && storedFormData) {
      reset(JSON.parse(JSON.stringify(storedFormData)) as DriverTripCalculatorFormData);
      hasRestoredRef.current = true;
    }
  }, [storedFormData, reset]);

  // Watch values
  const watchedVehicleTypeId = watch("vehicle_type_id");
  const watchedSegments = watch("segments");

  // Handle vehicle type change - reset vehicle model
  const handleVehicleTypeChange = (vehicleTypeId: string) => {
    setValue("vehicle_type_id", vehicleTypeId);
    setValue("vehicle_model_id", "");
  };

  // Add new segment
  const handleAddSegment = () => {
    if (segmentFields.length >= 10) {
      toast.warning(t("toast-segment-max"));
      return;
    }
    appendSegment(createDefaultSegment(segmentFields.length + 1));
  };

  // Remove segment
  const handleRemoveSegment = (index: number) => {
    if (segmentFields.length <= 1) {
      toast.warning(t("toast-segment-min"));
      return;
    }
    removeSegment(index);
    // Update segment routes
    const updatedRoutes = { ...segmentRoutes };
    delete updatedRoutes[index];
    // Shift subsequent indexes
    const newRoutes: Record<number, SegmentRoute> = {};
    Object.keys(updatedRoutes).forEach((key) => {
      const k = Number(key);
      if (k > index) {
        newRoutes[k - 1] = updatedRoutes[k];
      } else {
        newRoutes[k] = updatedRoutes[k];
      }
    });
    dispatch(setSegmentRoutes(newRoutes));
  };

  // Update segment route info (called from TripLocationInput)
  const updateSegmentRoute = (
    segmentIndex: number,
    totalDistance: number,
    totalTime: number,
    pairs: LocationPairRoute[] = [],
  ) => {
    dispatch(setSegmentRoutes({
      ...segmentRoutes,
      [segmentIndex]: { totalDistance, totalTime, pairs },
    }));
  };

  // Go to next step
  const handleNextToStep2 = async () => {
    const isValid = await trigger([
      "customer",
      "temperature",
      "plate_number",
      "vehicle_type_id",
      "vehicle_model_id",
    ]);

    if (!isValid) {
      toast.error(t("toast-fill-required"));
      return;
    }

    dispatch(saveFormData(JSON.parse(JSON.stringify(getValues()))));
    dispatch(setCurrentStep(2));
  };

  // Calculate trip cost
  const handleCalculate = async (data: DriverTripCalculatorFormData) => {
    // Guard: every segment must have a calculated route before submitting
    for (let i = 0; i < data.segments.length; i++) {
      const route = segmentRoutes[i];
      if (!route || route.totalDistance === 0) {
        toast.error(t("toast-route-not-ready", { number: i + 1 }));
        return;
      }
    }

    try {
      const apiResult = await calculateTrip({
        tripCalculatorV1CalculateTripReq: toCalculateTripReq(data, segmentRoutes),
      }).unwrap();

      if (apiResult.data) {
        const result = fromCalculateTripData(apiResult.data);
        dispatch(saveFormData(JSON.parse(JSON.stringify(data))));
        dispatch(setCalculationResult(result));

        // --- Developer debug log ---
        console.group("[DTC] Calculation Result");
        console.log("Trip Header", {
          id: result.id,
          customer: result.customer,
          plate_number: result.plate_number,
          temperature: result.temperature,
          vehicle_type: result.vehicle_type,
          vehicle_model: result.vehicle_model,
          status: result.status,
          created_at: result.created_at,
        });
        result.segments.forEach((seg, i) => {
          console.group(`Segment ${i + 1} — ${seg.trip_type?.name ?? seg.trip_type_id}`);
          console.log("Route", seg.locations);
          console.log("Distance / Time", {
            distance_km: seg.distance_km,
            estimated_time_minutes: seg.estimated_time_minutes,
            recommended_time_minutes: seg.recommended_time_minutes,
            trip_days: seg.trip_days,
          });
          console.log("Rates", {
            driver_basic_rate: seg.driver_basic_rate,
            driver_insentif_rate: seg.driver_insentif_rate,
            avg_speed: seg.avg_speed,
            working_hours: seg.working_hours,
          });
          console.log("Fuel", {
            fuel_usage: seg.fuel_usage,
            fuel_price: seg.fuel_price,
            solar_perjalanan: seg.solar_perjalanan,
            total_freezer_fuel: seg.total_freezer_fuel,
            freezer_fuel_efficiency: seg.freezer_fuel_efficiency,
          });
          console.log("Costs", {
            total_basic_rate: seg.total_basic_rate,
            incentive_rate: seg.incentive_rate,
            driver_allowance: seg.driver_allowance,
            additional_costs: seg.additional_costs,
            segment_total: seg.segment_total,
          });
          console.groupEnd();
        });
        console.log("Grand Total", result.grand_total);
        console.groupEnd();
        // --- end debug log ---
      }
      dispatch(setCurrentStep(3));
      toast.success(t("toast-calculation-success"));
    } catch (error) {
      console.error("Calculation error:", error);
      toast.error(t("toast-calculation-error"));
    }
  };

  // Reset form
  const handleReset = () => {
    reset();
    dispatch(resetDtcState());
  };

  // Go back to step 1 (from step 2), preserving form data
  const handleBackToStep1 = () => {
    dispatch(saveFormData(JSON.parse(JSON.stringify(getValues()))));
    dispatch(setCurrentStep(1));
  };

  // Go back to step 2
  const handleBackToStep2 = () => {
    dispatch(setCurrentStep(2));
  };

  // Get total distance and time across all segments
  const getTotalRouteInfo = () => {
    let totalDistance = 0;
    let totalTime = 0;
    Object.values(segmentRoutes).forEach((route) => {
      totalDistance += route.totalDistance;
      totalTime += route.totalTime;
    });
    return { totalDistance, totalTime };
  };

  return {
    control,
    Controller,
    errors,
    handleSubmit,
    watch,
    setValue,

    segmentFields,
    appendSegment: handleAddSegment,
    removeSegment: handleRemoveSegment,

    currentStep,
    calculationResult,
    isCalculating,
    segmentRoutes,

    handleVehicleTypeChange,
    handleNextToStep2,
    handleCalculate,
    handleReset,
    handleBackToStep1,
    handleBackToStep2,
    updateSegmentRoute,
    getTotalRouteInfo,

    watchedVehicleTypeId,
    watchedSegments,
  };
};
